// src/Pages/Dashboard/Sales/AddDelivery_Challans.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "./AddDelivery_Challans.css";

const AddDelivery_Challans = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're editing an existing challan
  const urlParams = new URLSearchParams(location.search);
  const editId = urlParams.get("id");

  const [challanNo, setChallanNo] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [showCustomerWarning, setShowCustomerWarning] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    referenceNo: "",
    challanDate: new Date().toISOString().split("T")[0],
    challanType: "Job Work",
    items: [
      {
        id: Date.now(),
        description: "",
        qty: 1,
        rate: 0,
        amount: 0,
      },
    ],
    discountValue: 0,
    discountType: "%",
    adjustment: 0,
    customerNotes: "",
    termsConditions: "",
  });

  // Smart backend URL - works locally + deployed
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");

  // Fetch next challan number
  useEffect(() => {
    const fetchNextChallanNo = async () => {
      try {
        const res = await fetch(
          `${backendUrl}/api/delivery-challans/next-challan-no`,
          {
            credentials: "include",
          }
        );
        const data = await res.json();
        if (data.success) {
          setChallanNo(data.nextChallanNo);
        } else {
          setChallanNo("DC-000001");
        }
      } catch (err) {
        console.error("Failed to fetch challan number:", err);
        setChallanNo("DC-000001");
      }
    };

    if (!editId) {
      fetchNextChallanNo();
    }
  }, [backendUrl, editId]);

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoadingCustomers(true);
        const res = await fetch(`${backendUrl}/api/customers/get`, {
          credentials: "include",
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          setCustomers(data.data);
        }
      } catch (err) {
        console.error("Failed to load customers:", err);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, [backendUrl]);

  // Load existing challan if editing
  useEffect(() => {
    if (editId) {
      const loadChallan = async () => {
        try {
          const res = await fetch(
            `${backendUrl}/api/delivery-challans/${editId}`,
            {
              credentials: "include",
            }
          );
          const data = await res.json();
          if (data.success && data.data) {
            const c = data.data;
            setChallanNo(c.challanNo);
            setSelectedCustomer(c.customerName);
            setFormData({
              referenceNo: c.referenceNo || "",
              challanDate: c.challanDate?.split("T")[0] || "",
              challanType: c.challanType || "Job Work",
              items:
                c.items?.length > 0
                  ? c.items.map((item) => ({
                      ...item,
                      id: Date.now() + Math.random(),
                    }))
                  : [
                      {
                        id: Date.now(),
                        description: "",
                        qty: 1,
                        rate: 0,
                        amount: 0,
                      },
                    ],
              discountValue: c.discountValue || 0,
              discountType: c.discountType || "%",
              adjustment: c.adjustment || 0,
              customerNotes: c.customerNotes || "",
              termsConditions: c.termsConditions || "",
            });
          }
        } catch (err) {
          console.error("Failed to load challan:", err);
          alert("Could not load challan data");
        }
      };
      loadChallan();
    }
  }, [editId, backendUrl]);

  const getCustomerDisplayName = (cust) => {
    const fullName = [cust.firstName, cust.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary =
      fullName || cust.displayName || cust.companyName || "Unnamed Customer";
    const secondary =
      cust.companyName && cust.companyName !== primary
        ? ` (${cust.companyName})`
        : "";
    return primary + secondary;
  };

  // Item handlers
  const updateItem = (id, field, value) => {
    setFormData((prev) => {
      const updatedItems = prev.items.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "qty" || field === "rate") {
            updated.amount =
              Number(updated.qty || 0) * Number(updated.rate || 0);
          }
          return updated;
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });
  };

  const removeItem = (id) => {
    if (formData.items.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.id !== id),
    }));
  };

  const addItemRow = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now() + Math.random(),
          description: "",
          qty: 1,
          rate: 0,
          amount: 0,
        },
      ],
    }));
  };

  // Calculations
  const subtotal = formData.items.reduce((sum, i) => sum + (i.amount || 0), 0);
  const discountAmount =
    formData.discountType === "%"
      ? (subtotal * formData.discountValue) / 100
      : formData.discountValue;
  const total = subtotal - discountAmount + Number(formData.adjustment || 0);

  // Save challan
  const handleSave = async (status) => {
    if (!selectedCustomer) {
      setShowCustomerWarning(true);
      return;
    }

    setSaving(true);

    const challanData = {
      ...(editId && { _id: editId }),
      challanNo,
      customerName: selectedCustomer,
      referenceNo: formData.referenceNo,
      challanDate: formData.challanDate,
      challanType: formData.challanType,
      items: formData.items.map((i) => ({
        description: i.description,
        qty: i.qty,
        rate: i.rate,
        amount: i.amount,
      })),
      discountValue: formData.discountValue,
      discountType: formData.discountType,
      adjustment: formData.adjustment,
      customerNotes: formData.customerNotes,
      termsConditions: formData.termsConditions,
      total,
      status,
    };

    try {
      const res = await fetch(`${backendUrl}/api/delivery-challans/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(challanData),
      });

      const data = await res.json();

      if (data.success) {
        // This triggers refresh on list page
        navigate("/sales/delivery-challans", { state: { refresh: true } });
      } else {
        alert("Save failed: " + (data.error || "Unknown error"));
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("Network error. Please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-delivery-challan-page">
      <div className="challan-header">
        <h1>{editId ? "Edit Delivery Challan" : "New Delivery Challan"}</h1>
      </div>

      <div className="challan-form">
        {/* Top Row */}
        <div className="form-row top-row">
          <div className="form-group customer-group">
            <label>
              Customer Name <span className="required">*</span>
            </label>
            <select
              value={selectedCustomer}
              onChange={(e) => {
                setSelectedCustomer(e.target.value);
                setShowCustomerWarning(false);
              }}
              disabled={loadingCustomers}
              className="customer-select"
            >
              <option value="">
                {loadingCustomers
                  ? "Loading customers..."
                  : "Select a customer"}
              </option>
              {customers.map((cust) => (
                <option key={cust._id} value={getCustomerDisplayName(cust)}>
                  {getCustomerDisplayName(cust)}
                </option>
              ))}
            </select>
            <div className="add-customer-link">
              <Link to="/sales/customers/new" className="link">
                + Add New Customer
              </Link>
            </div>
            {showCustomerWarning && (
              <small className="warning">Please select a customer</small>
            )}
          </div>

          <div className="form-group">
            <label>Delivery Challan#</label>
            <input type="text" value={challanNo} readOnly />
          </div>
        </div>

        {/* Reference, Date, Type */}
        <div className="form-row">
          <div className="form-group">
            <label>Reference#</label>
            <input
              type="text"
              value={formData.referenceNo}
              onChange={(e) =>
                setFormData({ ...formData, referenceNo: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Challan Date</label>
            <input
              type="date"
              value={formData.challanDate}
              onChange={(e) =>
                setFormData({ ...formData, challanDate: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>
              Challan Type <span className="required">*</span>
            </label>
            <select
              value={formData.challanType}
              onChange={(e) =>
                setFormData({ ...formData, challanType: e.target.value })
              }
            >
              <option>Job Work</option>
              <option>Supply on Approval</option>
              <option>Others</option>
            </select>
          </div>
        </div>

        {/* Item Table */}
        <div className="item-section">
          <div className="section-header">
            <h3>Item Table</h3>
            <div className="actions">
              <button className="action-btn" disabled>
                Scan Item
              </button>
              <button className="action-btn" disabled>
                Bulk Actions
              </button>
            </div>
          </div>

          <table className="item-table">
            <thead>
              <tr>
                <th>ITEM DETAILS</th>
                <th>QUANTITY</th>
                <th>RATE</th>
                <th>AMOUNT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="text"
                      placeholder="Type or click to select"
                      value={item.description}
                      onChange={(e) =>
                        updateItem(item.id, "description", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) =>
                        updateItem(item.id, "qty", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.rate}
                      onChange={(e) =>
                        updateItem(item.id, "rate", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td className="amount">₹{item.amount.toFixed(2)}</td>
                  <td>
                    {formData.items.length > 1 && (
                      <button
                        className="delete-btn"
                        onClick={() => removeItem(item.id)}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="add-row-actions">
            <button onClick={addItemRow} className="add-row-btn">
              + Add New Row
            </button>
            <button className="add-bulk-btn" disabled>
              + Add Items in Bulk
            </button>
          </div>
        </div>

        {/* Calculations */}
        <div className="calculations">
          <div className="notes-group">
            <label>Customer Notes</label>
            <textarea
              value={formData.customerNotes}
              onChange={(e) =>
                setFormData({ ...formData, customerNotes: e.target.value })
              }
              placeholder="Visible to customer on challan"
            />
          </div>

          <div className="calc-left">
            <div className="calc-row">Sub Total</div>
            <div className="calc-row">
              <span>Discount</span>
              <input
                type="number"
                value={formData.discountValue}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountValue: Number(e.target.value) || 0,
                  })
                }
              />
              <select
                value={formData.discountType}
                onChange={(e) =>
                  setFormData({ ...formData, discountType: e.target.value })
                }
              >
                <option>%</option>
                <option>₹</option>
              </select>
            </div>
            <div className="calc-row">
              <span>Adjustment</span>
              <input
                type="number"
                value={formData.adjustment}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    adjustment: Number(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="calc-row total">Total (₹)</div>
          </div>

          <div className="calc-right">
            <div>₹{subtotal.toFixed(2)}</div>
            <div>-₹{discountAmount.toFixed(2)}</div>
            <div>₹{(formData.adjustment || 0).toFixed(2)}</div>
            <div className="grand-total">₹{total.toFixed(2)}</div>
          </div>
        </div>

        {/* Terms & Attachments */}
        <div className="bottom-section">
          <div className="terms-group">
            <label>Terms & Conditions</label>
            <textarea
              placeholder="Enter terms and conditions..."
              value={formData.termsConditions}
              onChange={(e) =>
                setFormData({ ...formData, termsConditions: e.target.value })
              }
            />
          </div>
          <div className="attach-group">
            <label>Attach File(s) to Challan</label>
            <div className="upload-box">
              <input type="file" multiple disabled />
              <p>Coming soon...</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-bar">
          <div className="left-actions">
            <button
              onClick={() => handleSave("DRAFT")}
              className="draft-btn"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save as Draft"}
            </button>
            <button
              onClick={() => handleSave("SENT")}
              className="send-btn"
              disabled={saving}
            >
              {saving ? "Sending..." : "Save and Send"}
            </button>
            <button
              onClick={() =>
                navigate("/sales/delivery-challans", {
                  state: { refresh: true },
                })
              }
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
          <div className="right-actions">
            <div className="total-info">
              <span>Total Amount: ₹{total.toFixed(2)}</span>
              <span>
                Total Quantity:{" "}
                {formData.items.reduce((s, i) => s + (i.qty || 0), 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDelivery_Challans;
